import { useEffect, useState, useRef } from 'react';

interface UseVirtualizedGridProps {
    totalItems: number;
    initialVisibleCount?: number;
    loadMoreThreshold?: number;
    reduceThreshold?: number;
    batchSize?: number;
    minVisibleCount?: number;
    enabled?: boolean;
    onVisibleCountChange?: (count: number) => void;
}

interface UseVirtualizedGridResult {
    visibleCount: number;
    startRef: React.RefObject<HTMLDivElement>;
    endRef: React.RefObject<HTMLDivElement>;
}

/**
 * A custom hook that implements virtualized scrolling for large lists of items.
 * It manages the number of visible items based on scroll position and provides
 * refs for tracking the start and end of the virtualized content.
 *
 * The hook uses scroll position to determine when to load more items or reduce
 * the number of visible items, optimizing performance for large datasets.
 *
 * @param {UseVirtualizedGridProps} props - Configuration options for the virtualized grid
 * @param {number} props.totalItems - Total number of items in the list
 * @param {number} [props.initialVisibleCount=15] - Initial number of items to render
 * @param {number} [props.loadMoreThreshold=0.8] - Scroll progress threshold (0-1) for loading more items
 * @param {number} [props.reduceThreshold=0.2] - Scroll progress threshold (0-1) for reducing visible items
 * @param {number} [props.batchSize=15] - Number of items to load/unload in each batch
 * @param {number} [props.minVisibleCount=15] - Minimum number of items to keep visible
 * @param {boolean} [props.enabled=true] - Whether virtualization is enabled
 * @param {(count: number) => void} [props.onVisibleCountChange] - Callback when visible count changes
 *
 * @returns {UseVirtualizedGridResult} Object containing:
 *   - visibleCount: Current number of visible items
 *   - startRef: Ref for the start marker element
 *   - endRef: Ref for the end marker element
 *
 * @example
 * ```tsx
 * const { visibleCount, startRef, endRef } = useVirtualizedGrid({
 *   totalItems: 1000,
 *   initialVisibleCount: 20,
 *   loadMoreThreshold: 0.8,
 *   reduceThreshold: 0.2,
 *   batchSize: 10,
 *   onVisibleCountChange: (count) => console.log(`Visible items: ${count}`)
 * });
 * ```
 */
export const useVirtualizedGrid = ({
    totalItems,
    initialVisibleCount = 15,
    loadMoreThreshold = 0.8,
    reduceThreshold = 0.2,
    batchSize = 15,
    minVisibleCount = 15,
    enabled = true,
    onVisibleCountChange,
}: UseVirtualizedGridProps): UseVirtualizedGridResult => {
    const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
    const startRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!enabled) return;

        const handleScroll = () => {
            if (!startRef.current || !endRef.current) return;

            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const startTop =
                startRef.current.getBoundingClientRect().top + window.scrollY;
            const endTop =
                endRef.current.getBoundingClientRect().top + window.scrollY;
            const contentHeight = endTop - startTop;
            const scrollProgress =
                (scrollPosition + windowHeight - startTop) / contentHeight;

            // Load more items when scrolled to threshold
            if (
                scrollProgress > loadMoreThreshold &&
                visibleCount < totalItems
            ) {
                const newCount = Math.min(visibleCount + batchSize, totalItems);
                setVisibleCount(newCount);
                onVisibleCountChange?.(newCount);
            }

            // Reduce items when scrolling up and near the top
            if (
                scrollProgress < reduceThreshold &&
                visibleCount > minVisibleCount
            ) {
                const newCount = Math.max(
                    visibleCount - batchSize,
                    minVisibleCount
                );
                setVisibleCount(newCount);
                onVisibleCountChange?.(newCount);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [
        visibleCount,
        totalItems,
        loadMoreThreshold,
        reduceThreshold,
        batchSize,
        minVisibleCount,
        enabled,
        onVisibleCountChange,
    ]);

    return {
        visibleCount: enabled ? visibleCount : totalItems,
        startRef,
        endRef,
    };
};
